import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, Star } from "lucide-react";
import { supabase } from "../lib/supabase";
import { generateId, getToday } from "../utils";
import { decodePesquisaToken } from "../lib/pesquisaLink";

const NAVY = "#251C59";
const FALLBACK = {
  cliente: { titulo: "Pesquisa de Satisfação — Cliente", perguntas: [] },
  fornecedor: { titulo: "Pesquisa de Satisfação — Parceiro da Rede", perguntas: [] },
};

export default function PublicPesquisa({ token }) {
  const info = decodePesquisaToken(token) || {};
  const audience = info.a === "fornecedor" ? "fornecedor" : "cliente";
  const preview = new URLSearchParams(window.location.search).get("preview") === "1";

  const [cfg, setCfg] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [nome, setNome] = useState("");
  const [contato, setContato] = useState("");
  const [nps, setNps] = useState(null);
  const [respostas, setRespostas] = useState({});
  const [clientesAtendidos, setClientesAtendidos] = useState("");
  const [porQue, setPorQue] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    supabase.from("pesquisa_config").select("*").eq("id", audience).maybeSingle()
      .then(({ data }) => { if (alive) { setCfg(data || FALLBACK[audience]); setLoadingCfg(false); } })
      .catch(() => { if (alive) { setCfg(FALLBACK[audience]); setLoadingCfg(false); } });
    return () => { alive = false; };
  }, [audience]);

  const perguntas = cfg?.perguntas || [];
  const setResp = (id, v) => setRespostas((p) => ({ ...p, [id]: v }));

  const submit = async () => {
    if (preview) return;
    setErr("");
    if (nps === null) return setErr("Escolha uma nota de 0 a 10.");
    setSaving(true);
    try {
      const id = generateId();
      let ip = null;
      try { const r = await fetch("https://api.ipify.org?format=json"); ip = (await r.json())?.ip || null; } catch { /* ignora */ }
      const respArr = perguntas.map((q) => ({ id: q.id, label: q.label, tipo: q.tipo, resposta: respostas[q.id] ?? "" }));
      const cat = nps >= 9 ? "promotor" : nps >= 7 ? "neutro" : "detrator";
      const row = {
        id, user_id: info.u || null, audience, origem: "link",
        client_id: info.id || null, partner_id: info.p || null,
        client_name: info.n || null, score: nps, category: cat,
        feedback: [porQue.trim(), respArr.filter((r) => r.tipo === "texto" && r.resposta).map((r) => `${r.label}: ${r.resposta}`).join(" | ")].filter(Boolean).join(" | "),
        respostas: respArr, respondente_nome: nome.trim() || null, respondente_contato: contato.trim() || null,
        clientes_atendidos: audience === "fornecedor" ? (clientesAtendidos.trim() || null) : null,
        survey_date: getToday(), created_at: getToday(),
        meta: { ip, ua: navigator.userAgent, ts: new Date().toISOString() },
      };
      const { error } = await supabase.from("satisfaction").insert(row);
      if (error) throw new Error(error.message);
      setDone(true);
    } catch (e) { setErr("Não foi possível enviar: " + (e.message || "tente novamente")); }
    finally { setSaving(false); }
  };

  const wrap = { minHeight: "100vh", background: "#f1f5f9", display: "flex", justifyContent: "center", padding: "24px 14px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif", color: "#334155" };
  const card = { width: "100%", maxWidth: 560, background: "#fff", borderRadius: 16, boxShadow: "0 10px 30px rgba(0,0,0,0.08)", overflow: "hidden", height: "fit-content" };
  const inp = { width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 15, outline: "none", boxSizing: "border-box", background: "#f8fafc" };

  if (done) {
    return <div style={wrap}><div style={{ ...card, padding: 32, textAlign: "center", maxWidth: 440 }}>
      <CheckCircle2 size={56} color="#059669" style={{ margin: "0 auto 14px" }} />
      <h2 style={{ margin: "0 0 8px", color: "#0f172a", fontSize: 20 }}>Obrigado!</h2>
      <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.5 }}>
        {nps !== null && nps <= 6
          ? "Sua resposta foi registrada e já chegou à gerência. Vamos entrar em contato para entender melhor e corrigir o que estiver ao nosso alcance."
          : "Sua resposta foi registrada. Ela nos ajuda a melhorar continuamente os serviços da rede InstaSolutions."}
      </p>
    </div></div>;
  }

  return (
    <div style={wrap}>
      <div style={card}>
        {preview && <div style={{ background: "#f59e0b", color: "#fff", textAlign: "center", fontSize: 12, fontWeight: 700, padding: 6 }}>PRÉVIA — o envio está desabilitado</div>}
        <div style={{ background: NAVY, padding: "22px 20px", textAlign: "center" }}>
          <img src="/logo-horizontal-branco.png" alt="InstaSolutions" style={{ height: 38, objectFit: "contain", maxWidth: "80%" }} />
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 700, marginTop: 10 }}>{cfg?.titulo || FALLBACK[audience].titulo}</div>
          {info.n && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, marginTop: 2 }}>{info.n}</div>}
          <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 11, marginTop: 6 }}>Leva menos de 1 minuto · pode responder sem se identificar</div>
        </div>

        <div style={{ padding: 20 }}>
          {loadingCfg ? (
            <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}><Loader2 size={22} className="spin" /></div>
          ) : (
            <>
              {/* NPS */}
              <Label>De 0 a 10, o quanto você recomendaria a InstaSolutions? *</Label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
                {Array.from({ length: 11 }, (_, n) => (
                  <button key={n} type="button" onClick={() => !preview && setNps(n)} style={{ width: 40, height: 40, borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 700, border: nps === n ? "none" : "1px solid #e2e8f0", background: nps === n ? (n >= 9 ? "#059669" : n >= 7 ? "#f59e0b" : "#ef4444") : "#fff", color: nps === n ? "#fff" : "#475569" }}>{n}</button>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#94a3b8", marginBottom: 14 }}><span>Não recomendaria</span><span>Recomendaria muito</span></div>

              {/* A pergunta muda com a nota. "Deixe um comentário" recebe
                  silêncio; perguntar o que deu errado a quem deu nota 4 recebe
                  resposta — e é a resposta que vale alguma coisa. */}
              {nps !== null && (
                <div style={{ marginBottom: 18, padding: "12px 14px", borderRadius: 12, background: nps >= 9 ? "#f0fdf4" : nps >= 7 ? "#fffbeb" : "#fef2f2", border: `1px solid ${nps >= 9 ? "#bbf7d0" : nps >= 7 ? "#fde68a" : "#fecaca"}` }}>
                  <Label>
                    {nps >= 9
                      ? "Que bom! O que mais faz diferença para você?"
                      : nps >= 7
                        ? "O que faltou para ser uma nota 10?"
                        : "Sentimos muito. O que deu errado?"}
                  </Label>
                  <textarea
                    style={{ ...inp, minHeight: 70, resize: "vertical", background: "#fff" }}
                    value={porQue}
                    onChange={(e) => setPorQue(e.target.value)}
                    placeholder={nps >= 7 ? "Pode ser em uma frase." : "Conte o que aconteceu — vamos tratar caso a caso."}
                  />
                  {nps <= 6 && (
                    <div style={{ fontSize: 11, color: "#991b1b", marginTop: 6, lineHeight: 1.4 }}>
                      Sua resposta vai direto para a gerência, e alguém entra em contato.
                    </div>
                  )}
                </div>
              )}

              {/* Perguntas dinâmicas */}
              {perguntas.map((q) => (
                <div key={q.id} style={{ marginBottom: 16 }}>
                  <Label>{q.label}</Label>
                  {q.tipo === "escala" ? (
                    <div style={{ display: "flex", gap: 6 }}>
                      {[1, 2, 3, 4, 5].map((v) => (
                        <button key={v} type="button" onClick={() => !preview && setResp(q.id, v)} style={{ flex: 1, padding: "10px 0", borderRadius: 8, cursor: "pointer", border: respostas[q.id] === v ? "none" : "1px solid #e2e8f0", background: respostas[q.id] === v ? "#7c3aed" : "#fff", color: respostas[q.id] === v ? "#fff" : "#475569", display: "flex", alignItems: "center", justifyContent: "center", gap: 3, fontSize: 12, fontWeight: 600 }}>
                          <Star size={12} fill={respostas[q.id] >= v ? "currentColor" : "none"} /> {v}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} value={respostas[q.id] || ""} onChange={(e) => setResp(q.id, e.target.value)} placeholder="Sua resposta..." />
                  )}
                </div>
              ))}

              {audience === "fornecedor" && (
                <div style={{ marginBottom: 16 }}>
                  <Label>Quais clientes você atende pela rede?</Label>
                  <textarea style={{ ...inp, minHeight: 50, resize: "vertical" }} value={clientesAtendidos} onChange={(e) => setClientesAtendidos(e.target.value)} placeholder="Ex: Prefeitura X, Empresa Y..." />
                </div>
              )}

              <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 8, paddingTop: 14 }}>
                <Label>Seu nome (opcional)</Label>
                <input style={inp} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome" />
                <div style={{ height: 12 }} />
                <Label>Contato — telefone/e-mail (opcional)</Label>
                <input style={inp} value={contato} onChange={(e) => setContato(e.target.value)} placeholder="Para retornarmos, se você quiser" />
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10, lineHeight: 1.45 }}>
                  🔒 Suas respostas ficam em servidor privado e são usadas apenas para melhorar o atendimento da rede,
                  conforme a LGPD. Você pode responder sem se identificar.
                </div>
              </div>

              {err && <div style={{ fontSize: 13, color: "#ef4444", marginTop: 12, fontWeight: 500 }}>{err}</div>}
              <button onClick={submit} disabled={saving || preview} style={{ width: "100%", marginTop: 18, padding: "13px", borderRadius: 12, border: "none", background: (saving || preview) ? "#a78bfa" : NAVY, color: "#fff", fontSize: 15, fontWeight: 700, cursor: (saving || preview) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                {saving ? <><Loader2 size={18} className="spin" /> Enviando...</> : preview ? "Envio desabilitado (prévia)" : "Enviar resposta"}
              </button>
            </>
          )}
        </div>
      </div>
      <style>{`.spin { animation: spin 1s linear infinite } @keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

function Label({ children }) { return <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>{children}</div>; }
